<template>
  <div class="cpe-renew-modal__wrapper">
    <div v-if="!isLoading" class="cpe-renew-modal">
      <RenewModalOptions
        v-if="step === modalSteps.OPTIONS"
        :certification="certification"
        :items="itemsAboveCurrent"
        :purchasableItems="purchasableItemsAboveCurrent"
        :userActiveVouchers="userActiveVouchers"
        :certPurchasableItem="certPurchasableItem"
      />
      <RenewModalPurchase
        v-if="step === modalSteps.PURCHASE"
        :infoText="purchaseInfoText"
        :items="purchasableItemsAboveCurrent"
      />
      <RenewModalPayment
        v-if="step === modalSteps.PAYMENT"
        @close-modal="closeModal"
      />
      <RenewModalSuccess v-if="step === modalSteps.SUCCESS" />
    </div>
    <ActionLoader v-else class="cpe-renew-modal--loading" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import CONSTANTS from '@/constants'
import RenewModalOptions from './RenewModalOptions.vue'
import RenewModalPurchase from './RenewModalPurchase.vue'
import RenewModalPayment from './RenewModalPayment.vue'
import RenewModalSuccess from './RenewModalSuccess.vue'
import ActionLoader from '@/components/shared/ActionLoader.vue'
import { certificationCommons } from '@/helpers/mixins'
import { plansCPEHydratation } from '@/helpers/hydratations'

const { STEPS, UNALLOWED_CERTIFICATIONS, CERT_EXPIRATION_DAYS_TO_ADD } = CONSTANTS.CPE
const {
  IDS_MERCHANT_IDS,
  CERT_MERCHANT_PLAN_MERCHANT,
} = CONSTANTS.CERTIFICATIONS

export default {
  name: 'RenewModal',
  components: {
    RenewModalOptions,
    RenewModalPurchase,
    RenewModalPayment,
    RenewModalSuccess,
    ActionLoader,
  },
  mixins: [certificationCommons],
  props: {
    certification: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      certPurchasableItem: {},
      purchasablePlans: [],
      upperLevelCertificationsAvailable: [],
    }
  },
  computed: {
    ...mapGetters({
      cpeGlobalCredits: 'cpeProgram/cpeGlobalCredits',
      renewModalStatus: 'cpeProgram/cpeRenewModalStatus',
      userVouchers: 'certifications/usersVouchers',
      productCertificationVouchers:
        'subscriptions/getProductCertificationVouchers',
      isBusinessUser: 'business/isBusinessUser',
      activeSubscriptions: 'subscriptions/activeSubscriptions',
      currentAvailablePlans: 'subscriptions/currentAvailablePlans',
    }),
    modalSteps() {
      return STEPS
    },
    step() {
      return this.renewModalStatus.step
    },
    userActiveVouchers() {
      return this.userVouchers.filter(
        voucher => voucher.user_status.active_vouchers.length > 0
      )
    },
    userVouchersIds() {
      return this.userActiveVouchers.flatMap(voucher =>
        voucher.versions.map(version => version.id)
      )
    },
    certificationVersionsVoucherIds() {
      return this.certification.versions.map(v => v.id)
    },
    upperLevelVouchersIdsAvailable() {
      return this.upperLevelCertificationsAvailable
        .map(
          v =>
            this.productCertificationVouchers.find(
              voucher => voucher.merchant_id === IDS_MERCHANT_IDS[v.id]
            )?.id
        )
        .filter(Boolean)
    },
    purchasablePlanMerchantIds() {
      return this.purchasablePlans.map(
        p => CERT_MERCHANT_PLAN_MERCHANT[IDS_MERCHANT_IDS[p.id]]
      )
    },
    itemsAboveCurrent() {
      let plans = []
      const vouchers = this.productCertificationVouchers
        .map(v => {
          if (this.upperLevelVouchersIdsAvailable.includes(v.id))
            return {
              ...v,
              type: 'voucher',
            }
          return false
        })
        .filter(Boolean)

      if (this.purchasablePlanMerchantIds.length) {
        plans = this.currentAvailablePlans
          .map(currentPlan => {
            const plan = this.purchasablePlans.find(
              p =>
                CERT_MERCHANT_PLAN_MERCHANT[IDS_MERCHANT_IDS[p.id]] ===
                currentPlan.merchant_id
            )
            if (!plan) return false
            return {
              ...currentPlan,
              name: `${plan.short_name}*`,
              plan_name: currentPlan.name,
              type: 'plan',
            }
          })
          .filter(Boolean)
      }
      return [...plans, ...vouchers]
    },
    purchaseInfoText() {
      return this.renewModalStatus.isCurrentCert
        ? `Pass the latest ${this.certification.short_name} exam to renew your
        certification.`
        : `By earning a more advanced certification, you’ll be eligible to renew your ${this.certification.short_name}. Select one of the more advanced exam vouchers below.`
    },
    purchasableItemsAboveCurrent() {
      const { VOUCHER_NAMES } = CONSTANTS
      const { isCurrentCert, currentSelection } = this.renewModalStatus
      const items = isCurrentCert ? [currentSelection] : this.itemsAboveCurrent
      return items
        ?.map(v => {
          const {
            id,
            merchant_id,
            name,
            type,
            price,
            user_receives_discount,
            discounted_price,
          } = v || {}
          const currentPrice = user_receives_discount ? discounted_price : price

          if (type === 'plan') {
            const plan = plansCPEHydratation(v?.merchant_id)
            return {
              ...v,
              name: `${plan.short_name} Certification ${plan.info}`,
              label: {
                text: `${plan.text}`,
                info: currentPrice,
                footnote: `${plan.infoExtra}`,
              },
            }
          }

          const notInUpperLevel = !this.upperLevelVouchersIdsAvailable.includes(
            id
          )
          const userAlreadyHasVoucher = this.userVouchersIds.includes(id)
          if (!isCurrentCert && (notInUpperLevel || userAlreadyHasVoucher))
            return false

          const text = VOUCHER_NAMES[merchant_id]
            ? `${VOUCHER_NAMES[merchant_id]} exam voucher`
            : ''
          return {
            ...v,
            name: `${name} exam voucher`,
            short_name: name,
            label: {
              text,
              info: currentPrice,
            },
          }
        })
        .filter(Boolean)
    },
    futureDate() {
      const future = new Date(this.certification.cert_awarded_expiration_date)
      future.setDate(future.getDate() + CERT_EXPIRATION_DAYS_TO_ADD)
      return future.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    },
  },
  async created() {
    this.isRenewalAvailableCalculator(this.certification)
    const nextStep = this.isRenewalAvailableHigherCert
      ? STEPS.PAYMENT
      : STEPS.OPTIONS
    this.setRenewModalStatus({
      step: nextStep,
      prevStep: null,
      currentSelection: null,
      currentOption: null,
      isCurrentCert: false,
      voucher: null,
      certification: this.certification,
      isRenewal: this.isRenewalAvailableHigherCert,
      renewalDate: this.futureDate,
    })
    await this.fetchData()
  },
  methods: {
    ...mapActions({
      setRenewModalStatus: 'cpeProgram/setRenewModalStatus',
      fetchAvailablePlans: 'subscriptions/fetchAvailablePlans',
    }),
    setupItems() {
      this.certPurchasableItem = {
        ...this.productCertificationVouchers.find(
          v => IDS_MERCHANT_IDS[this.certification.id] === v.merchant_id
        ),
        type: 'voucher',
      }

      if (this.activeSubscriptions.length || this.isBusinessUser) {
        this.upperLevelCertificationsAvailable = this.certification.upper_level_certifications
        return
      }

      // for earning option
      this.certification.upper_level_certifications.forEach(cert => {
        if (UNALLOWED_CERTIFICATIONS.includes(cert.id)) {
          this.purchasablePlans.push(cert)
        } else {
          this.upperLevelCertificationsAvailable.push(cert)
        }
      })

      // for renewal option
      if (UNALLOWED_CERTIFICATIONS.includes(this.certification.id)) {
        const certMerchantId =
          CERT_MERCHANT_PLAN_MERCHANT[IDS_MERCHANT_IDS[this.certification.id]]
        this.certPurchasableItem = {
          ...this.currentAvailablePlans.find(
            p => certMerchantId === p.merchant_id
          ),
          type: 'plan',
        }
      }
    },
    async fetchData() {
      try {
        this.isLoading = true
        await this.fetchAvailablePlans()
        this.setupItems()
      } catch (error) {
        this.$emit('close-modal')
      } finally {
        this.isLoading = false
      }
    },
    closeModal() {
      this.$emit('close-modal')
    },
  },
}
</script>

<style lang="scss" scoped>
.cpe-renew-modal {
  overflow: unset;
  &--loading {
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0;
    top: -40px;
    position: relative;
    height: 60vh;

    @include mobile {
      min-width: 100%;
    }
    @include tablet {
      width: 705px;
    }
  }
  &__wrapper {
    width: 98vw;
    max-width: 705px;
    max-height: 90vh;
    padding: 30px 0 0 0;
    color: var(--white);
    min-width: 100%;
    @include mobile {
      min-width: 100%;
    }
    @include tablet {
      width: 705px;
    }
  }
  &__container {
    display: flex;
    flex-direction: column;
    gap: 40px;
  }
  &__header {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  &__subtitle,
  &__info {
    margin-top: 20px;
    color: var(--light-blue-gray);
    font-size: rem(16);
    line-height: rem(24);
    text-align: center;
  }
  &__cta-wrapper {
    display: flex;
  }
  &__footer {
    $offset: 30px;
    position: relative;
    left: -$offset;
    width: calc(100% + #{$offset * 2});
    margin-top: 60px;
    &-container {
      display: flex;
      gap: 10px;
      justify-content: space-between;
      padding: 30px 40px;
      background: var(--glass-mid-gray);
    }
    &-info {
      &-container {
        display: flex;
        flex-direction: column;
        justify-content: center;
      }
      &-price {
        font-size: rem(20);
        font-weight: 700;
      }
      &-text {
        font-size: rem(10);
        color: var(--light-blue-gray);
      }
    }
    &-cta-container {
      display: flex;
      gap: 10px;
    }
  }
}
</style>
